<template>
  <NuxtLayout name="auth">
    <div
      class="relative h-full w-full flex items-center justify-center px-6 overflow-hidden min-h-screen bg-black"
    >
      <div class="absolute inset-0 pointer-events-none">
        <div
          :class="[
            'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full blur-[120px] rounded-full animate-pulse transition-colors duration-1000',
            isNotFound ? 'bg-brand/5' : 'bg-red-500/5',
          ]"
        />
      </div>

      <div class="relative z-10 max-w-2xl text-center space-y-8">
        <div
          :class="[
            'inline-flex items-center gap-3 px-5 py-2 rounded-full border backdrop-blur-md transition-colors duration-500',
            isNotFound
              ? 'bg-brand/10 border-brand/20 text-brand'
              : 'bg-red-500/10 border-red-500/20 text-red-500',
          ]"
        >
          <component :is="errorIcon" class="size-4" />
          <span class="text-[10px] font-black tracking-[0.3em] uppercase">
            {{
              isNotFound
                ? "System Alert: Page Missing"
                : "System Alert: Execution Fault"
            }}
          </span>
        </div>

        <div class="space-y-4">
          <h1
            class="text-5xl md:text-7xl font-black text-white tracking-tighter leading-[0.9]"
          >
            {{ isNotFound ? "Location" : "Application" }} <br />
            <span :class="isNotFound ? 'text-brand' : 'text-red-500'">
              {{ isNotFound ? "Unavailable." : "Disrupted." }}
            </span>
          </h1>

          <p
            class="text-xs md:text-sm font-bold text-white/40 tracking-[0.4em] leading-relaxed max-w-md mx-auto"
          >
            {{ errorSummary }}
          </p>
        </div>

        <div class="pt-8 space-y-6">
          <div
            class="p-6 rounded-2xl bg-white/2 border border-white/5 backdrop-blur-3xl text-left sm:text-center"
          >
            <p
              class="text-[10px] font-black text-white/20 tracking-widest uppercase mb-2"
            >
              Recommended Action
            </p>
            <p class="text-xs font-bold text-white/60 tracking-wide">
              {{ errorActionMessage }}
            </p>

            <div
              v-if="isDevelopment && error?.message"
              class="mt-4 p-4 rounded-xl bg-black/40 border border-white/5 text-left font-mono text-[11px] text-red-400/80 overflow-x-auto max-h-40 custom-scrollbar"
            >
              <div
                class="font-bold text-white/40 mb-1 text-[9px] tracking-wider uppercase"
              >
                Diagnostic Trace:
              </div>
              {{ error.message }}
            </div>
          </div>

          <div
            class="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button
              @click="handleClearError"
              class="w-full sm:w-auto px-10 py-4 rounded-2xl bg-brand text-black text-[11px] font-black tracking-[0.2em] transition-all hover:scale-105 active:scale-95 shadow-2xl shadow-brand/20 cursor-pointer"
            >
              Return to Safety
            </button>
          </div>
        </div>
      </div>

      <div class="absolute bottom-12 left-1/2 -translate-x-1/2 opacity-20">
        <span
          class="text-[9px] font-mono text-white tracking-[0.5em] uppercase"
        >
          Error_Code: {{ error?.statusCode || 500 }}_{{ errorStatusText }}
        </span>
      </div>
    </div>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import { Compass, AlertTriangle } from "lucide-vue-next";
import type { NuxtError } from "#app";

// Core Props Definition for Nuxt 4 Error handling
const props = defineProps({
  error: {
    type: Object as () => NuxtError,
    default: null,
  },
});

// Runtime Environment Verification
const config = useRuntimeConfig();
const isDevelopment = process.env.APP_ENV === "development" || import.meta.dev;

// Computed Properties for Context Filtering
const isNotFound = computed(() => props.error?.statusCode === 404);

const errorIcon = computed(() => (isNotFound.value ? Compass : AlertTriangle));

const errorStatusText = computed(() => {
  if (props.error?.statusCode === 404) return "NOT_FOUND";
  if (props.error?.statusCode === 403) return "FORBIDDEN";
  return "INTERNAL_SERVER_ERROR";
});

const errorSummary = computed(() => {
  if (isNotFound.value) {
    return "The requested parameters do not align with any known active route coordinates.";
  }
  return "An unhandled execution event has interrupted core pipeline routines.";
});

const errorActionMessage = computed(() => {
  if (isNotFound.value) {
    return "Verify the source destination path details or return back to standard terminal environments.";
  }
  return "Refresh interface instance state. Persistent failures indicate systemic API maintenance timelines.";
});

// Clean Framework App Instance Errors State & Reroute Home safely
const handleClearError = () => {
  clearError({ redirect: "/" });
};
</script>

<style scoped>
h1 {
  font-family: "Inter Tight", sans-serif;
  -webkit-text-stroke: 1px rgba(255, 255, 255, 0.05);
}

.animate-pulse {
  animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.1;
  }
  50% {
    opacity: 0.3;
  }
}

/* Custom technical logging scrollbar styling */
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 9999px;
}
</style>
